import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  IcnArrowRight,
  IcnSearch,
  SearchInput,
  Spinner,
} from '@viquard/doc-ui';
import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SearchResultCard,
  useSearch,
  useSearchSuggestions,
  type SearchHit,
  type SearchSuggestion,
} from '@/features/search';

const RESULT_LIMIT = 8;

export interface ArticleSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ArticleSearchDialog(props: ArticleSearchDialogProps): ReactNode {
  const { open, onOpenChange } = props;
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const normalized = debouncedQuery.trim();

  const suggestions = useSearchSuggestions(
    { q: normalized, scope: 'all', limit: RESULT_LIMIT },
    { enabled: open && normalized.length >= 2 },
  );
  const fallbackResults = useSearch(
    { q: normalized, scope: 'all', limit: RESULT_LIMIT, offset: 0 },
    { enabled: open && normalized.length >= 2 && suggestions.isError },
  );
  const suggestionData = suggestions.data;
  const fallbackHits = fallbackResults.data?.hits;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const openFullSearch = (): void => {
    const q = query.trim();
    if (q.length === 0) return;
    onOpenChange(false);
    void navigate({ to: '/search', search: { q } });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    openFullSearch();
  };

  const rows = useMemo(() => {
    const rawRows = suggestionData !== undefined
      ? suggestionData.suggestions.map(rowFromSuggestion)
      : fallbackHits?.map(rowFromHit) ?? [];
    return dedupeDialogRows(rawRows);
  }, [fallbackHits, suggestionData]);
  const isPending = suggestions.isPending || (suggestions.isError && fallbackResults.isPending);
  const isError = suggestions.isError && fallbackResults.isError;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('search.dialogTitle')}
      subtitle={t('search.dialogSubtitle')}
      tone="info"
      toneIcon={<IcnSearch size={18} />}
      size="lg"
      footer={
        <Button
          type="button"
          variant="primary"
          size="sm"
          iconLeft={<IcnArrowRight size={14} />}
          disabled={query.trim().length === 0}
          onClick={openFullSearch}
        >
          {t('search.openFull')}
        </Button>
      }
    >
      <div className="site-search-dialog">
        <SearchInput
          autoFocus
          size="lg"
          value={query}
          placeholder={t('search.inputPlaceholder')}
          onValueChange={setQuery}
          onDebouncedChange={setDebouncedQuery}
          onKeyDown={onKeyDown}
        />

        {normalized.length < 2 ? (
          <div className="site-search-dialog__empty">{t('search.minHint')}</div>
        ) : isPending ? (
          <div className="site-search-dialog__pending" role="status" aria-live="polite">
            <Spinner size="sm" />
            <span>{t('search.loading')}</span>
          </div>
        ) : isError ? (
          <div className="site-search-dialog__empty">{t('search.failed')}</div>
        ) : rows.length === 0 ? (
          <div className="site-search-dialog__empty">{t('search.empty')}</div>
        ) : (
          <ul className="site-search-dialog__results" role="list">
            {rows.map((row) => (
              <li key={`${row.scope}:${row.id}`}>
                <SearchResultCard
                  scope={row.scope}
                  title={row.title}
                  summary={row.summary}
                  status={row.status}
                  metadata={row.metadata}
                  url={row.url}
                  query={normalized}
                  variant="dialog"
                  onOpen={() => {
                    onOpenChange(false);
                    window.location.assign(row.url);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

interface DialogSearchRow {
  id: string;
  scope: SearchHit['scope'];
  title: string;
  summary: string | null;
  status: string | null;
  metadata: Record<string, unknown>;
  url: string;
}

function rowFromSuggestion(suggestion: SearchSuggestion): DialogSearchRow {
  return {
    id: suggestion.resource.id,
    scope: suggestion.scope,
    title: suggestion.label,
    summary: suggestion.resource.summary,
    status: suggestion.resource.status,
    metadata: suggestion.resource.metadata,
    url: suggestion.url,
  };
}

function rowFromHit(hit: SearchHit): DialogSearchRow {
  return {
    id: hit.resource.id,
    scope: hit.scope,
    title: hit.resource.title,
    summary: hit.resource.summary,
    status: hit.resource.status,
    metadata: hit.resource.metadata,
    url: hit.resource.url,
  };
}

function dedupeDialogRows(rows: readonly DialogSearchRow[]): DialogSearchRow[] {
  const seen = new Set<string>();
  const out: DialogSearchRow[] = [];
  for (const row of rows) {
    const key = `${row.scope}:${row.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}
